import React from "react";
import * as Yup from "yup";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { Col, Spinner } from "reactstrap";
import classNames from "classnames";
import FormGenerator from "components/customs/form-generator/input";
import CustomSelect from "components/customs/dropdown";
import { CONGRESS_OPTIONS } from "utility/constants";
import { addProjectDropdownStyles } from "constants/style";
import { useNavigate } from "react-router";
import {
  FormActionsSection,
  FormRow,
  RequiredIndicator,
  StyledCancelButton,
  StyledSaveButton,
} from "assets/js/Styles";
import styled from "styled-components";
import { baseValidationSchema, mobileValidationSchema } from "validation";

const validSchema = baseValidationSchema.concat(mobileValidationSchema).shape({
  congress: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.string().required(),
      })
    )
    .min(1, "At least one congress must be selected")
    .required("Required"),
  district: Yup.object().required("Required"),
  party: Yup.object().required("Required"),
});

const CongressmanFiledContainer = styled(FormRow)`
  margin-top: 0;
`;

const CongressmanForm = ({
  handleOnSubmit,
  formFields,
  setFormFields,
  districtOptions,
  partylists,
  handleCongressSelect,
  isLoading,
}) => {
  const navigate = useNavigate();

  return (
    <Formik
      validationSchema={validSchema}
      initialValues={formFields}
      enableReinitialize
      onSubmit={handleOnSubmit}
    >
      {({ handleChange, values }) => (
        <Form>
          <Field name="congress">
            {({ form: { touched, errors } }) => (
              <CongressmanFiledContainer>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Congressman"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="congress"
                    isMulti
                    styles={addProjectDropdownStyles}
                    className={classNames({
                      "is-invalid": errors?.congress && touched?.congress,
                    })}
                    options={CONGRESS_OPTIONS}
                    value={values.congress}
                    onChange={handleCongressSelect}
                    getOptionLabel={(option) => {
                      return option?.value;
                    }}
                  />
                  <ErrorMessage name="congress">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </CongressmanFiledContainer>
            )}
          </Field>

          <Field name="lastname">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Last Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="lastname"
                    className={classNames({
                      "is-invalid": errors?.lastname && touched?.lastname,
                    })}
                    value={values.lastname}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        lastname: evt.target.value || "",
                      });
                    }}
                  />

                  <ErrorMessage name="lastname">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="firstname">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"First Name"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="firstname"
                    className={classNames({
                      "is-invalid": errors?.firstname && touched?.firstname,
                    })}
                    value={values.firstname}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        firstname: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="firstname">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="middleInitial">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Middle Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="middleInitial"
                    className={classNames({
                      "is-invalid":
                        errors?.middleInitial && touched?.middleInitial,
                    })}
                    value={values.middleInitial}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        middleInitial: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="middleInitial">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="mobile">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Contact Number"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    name="mobile"
                    type="mobile"
                    className={classNames({
                      "is-invalid": errors?.mobile && touched?.mobile,
                    })}
                    value={values.mobile || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        mobile: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="mobile">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="district">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"District"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="district"
                    styles={addProjectDropdownStyles}
                    options={districtOptions}
                    value={values.district}
                    className={classNames({
                      "is-invalid": errors?.district && touched?.district,
                    })}
                    onChange={(option) => {
                      setFormFields({
                        ...formFields,
                        district: option,
                      });
                    }}
                  />
                  <ErrorMessage name="district">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="party">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>
                    {"Party"}
                    {RequiredIndicator}
                  </h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <CustomSelect
                    placeholder=""
                    name="party"
                    styles={addProjectDropdownStyles}
                    options={partylists}
                    value={values.party}
                    className={classNames({
                      "is-invalid": errors?.party && touched?.party,
                    })}
                    onChange={(option) => {
                      setFormFields({
                        ...formFields,
                        party: option,
                      });
                    }}
                  />
                  <ErrorMessage name="party">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosFirstName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Chief of Staff First Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosFirstName"
                    className={classNames({
                      "is-invalid":
                        errors?.cosFirstName && touched?.cosFirstName,
                    })}
                    value={values.cosFirstName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosFirstName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosFirstName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosLastName">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Chief of Staff Last Name"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="text"
                    name="cosLastName"
                    className={classNames({
                      "is-invalid": errors?.cosLastName && touched?.cosLastName,
                    })}
                    value={values.cosLastName}
                    onChange={(evt) => {
                      handleChange(evt);

                      setFormFields({
                        ...formFields,
                        cosLastName: evt.target.value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosLastName">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <Field name="cosNumber">
            {({ form: { touched, errors } }) => (
              <FormRow>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <h5>{"Contact Number"}</h5>
                </Col>
                <Col
                  xs="12"
                  sm="6"
                  md="12"
                  lg="6"
                >
                  <FormGenerator
                    type="mobile"
                    name="cosNumber"
                    className={classNames({
                      "is-invalid": errors?.cosNumber && touched?.cosNumber,
                    })}
                    value={values.cosNumber || ""}
                    onChange={(value) => {
                      handleChange(value);

                      setFormFields({
                        ...formFields,
                        cosNumber: value || "",
                      });
                    }}
                  />
                  <ErrorMessage name="cosNumber">
                    {(msg) => (
                      <small className="field-error text-danger">{msg}</small>
                    )}
                  </ErrorMessage>
                </Col>
              </FormRow>
            )}
          </Field>

          <FormActionsSection>
            <StyledCancelButton
              outline
              disabled={isLoading}
              onClick={() => navigate("/directory")}
            >
              {"Cancel"}
            </StyledCancelButton>

            <StyledSaveButton
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  animation="border"
                  color="light"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </StyledSaveButton>
          </FormActionsSection>
        </Form>
      )}
    </Formik>
  );
};

export default CongressmanForm;
